import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { toast } from 'sonner';
import { Sparkles, Plus, Trash2, Edit, Loader2, Lightbulb, ArrowRight, X } from 'lucide-react';
import type { Question, QuestionType, SkillTestDraft, SkillTestStatus } from '../../types/skillTests';
import { generateAiQuestions } from '../../utils/skillTestGenerator';

type QuestionDraft = Omit<Question, 'id'> & { id?: number };

const categories = [
  'Customer Service',
  'Mathematics',
  'Health & Safety',
  'Communication',
  'Technical Skills',
  'Problem Solving',
  'Operations',
  'Leadership',
] as const;

const statuses: SkillTestStatus[] = ['draft', 'active', 'archived'];

const createEmptyQuestion = (): QuestionDraft => ({
  id: undefined,
  text: '',
  type: 'multiple-choice',
  options: ['', '', '', ''],
  correctAnswer: '',
  points: 10,
});

const cloneQuestionsForUndo = (questions: SkillTestDraft['questions']) =>
  questions.map((question) => ({
    ...question,
    options: question.options ? [...question.options] : undefined,
  }));

const ensureOptions = (question: QuestionDraft): QuestionDraft => {
  if (question.type === 'multiple-choice') {
    return {
      ...question,
      options: question.options && question.options.length === 4 ? question.options : ['', '', '', ''],
    };
  }
  if (question.type === 'true-false') {
    return {
      ...question,
      options: ['True', 'False'],
      correctAnswer: question.correctAnswer === 'false' ? 'false' : 'true',
    };
  }
  return { ...question, options: undefined };
};

interface SkillTestBuilderProps {
  mode: 'create' | 'edit';
  initialValue: SkillTestDraft;
  onSubmit: (draft: SkillTestDraft) => void;
  onCancel: () => void;
}

export function SkillTestBuilder({ mode, initialValue, onSubmit, onCancel }: SkillTestBuilderProps) {
  const [form, setForm] = useState<SkillTestDraft>(initialValue);
  const [questionDraft, setQuestionDraft] = useState<QuestionDraft>(() => ensureOptions(createEmptyQuestion()));
  const [jobSpec, setJobSpec] = useState('');
  const [aiQuestionCount, setAiQuestionCount] = useState(6);
  const [aiGenerating, setAiGenerating] = useState(false);

  const handleFormChange = <Key extends keyof SkillTestDraft>(key: Key, value: SkillTestDraft[Key]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleQuestionTypeChange = (type: QuestionType) => {
    setQuestionDraft((prev) => ensureOptions({ ...prev, type, correctAnswer: '' }));
  };

  const handleQuestionOptionChange = (index: number, value: string) => {
    setQuestionDraft((prev) => {
      if (!prev.options) {
        return prev;
      }
      const options = [...prev.options];
      const previousValue = options[index];
      options[index] = value;
      return {
        ...prev,
        options,
        correctAnswer: prev.correctAnswer === previousValue ? value : prev.correctAnswer,
      };
    });
  };

  const addQuestionToForm = () => {
    if (!questionDraft.text.trim()) {
      toast.error('Please enter a question prompt before adding it to the test.');
      return;
    }

    if (questionDraft.type === 'multiple-choice') {
      if (!questionDraft.options?.every((option) => option.trim())) {
        toast.error('Please fill in all answer options before saving.');
        return;
      }
      const mcCorrect = typeof questionDraft.correctAnswer === 'string' ? questionDraft.correctAnswer : '';
      if (!mcCorrect || !questionDraft.options.includes(mcCorrect)) {
        toast.error('Select a correct answer for the multiple choice question.');
        return;
      }
    }

    const questionId = questionDraft.id ?? Date.now();
    const previousQuestions = cloneQuestionsForUndo(form.questions);

    const question: Question = {
      id: questionId,
      text: questionDraft.text.trim(),
      type: questionDraft.type,
      options:
        questionDraft.type === 'multiple-choice'
          ? [...(questionDraft.options ?? [])]
          : questionDraft.type === 'true-false'
            ? ['True', 'False']
            : undefined,
      correctAnswer:
        questionDraft.type === 'multiple-choice'
          ? (typeof questionDraft.correctAnswer === 'string'
              ? questionDraft.correctAnswer
              : (questionDraft.options?.[0] ?? ''))
          : questionDraft.type === 'true-false'
            ? (typeof questionDraft.correctAnswer === 'string' ? questionDraft.correctAnswer : 'true')
            : questionDraft.correctAnswer,
      points: questionDraft.points || 10,
    };

    const updatedQuestions = [
      ...previousQuestions.filter((q) => q.id !== questionId),
      question,
    ];

    setForm((prev) => ({
      ...prev,
      questions: updatedQuestions,
    }));
    setQuestionDraft(ensureOptions(createEmptyQuestion()));
    toast.success('Question added to the test.', {
      duration: 4000,
      action: {
        label: 'Undo',
        onClick: () =>
          setForm((prev) => ({
            ...prev,
            questions: previousQuestions,
          })),
      },
    });
  };

  const removeQuestion = (id: number) => {
    const previousQuestions = cloneQuestionsForUndo(form.questions);

    setForm((prev) => ({
      ...prev,
      questions: prev.questions.filter((question) => question.id !== id),
    }));
    toast.success('Question removed.', {
      duration: 4000,
      action: {
        label: 'Undo',
        onClick: () =>
          setForm((prev) => ({
            ...prev,
            questions: previousQuestions,
          })),
      },
    });
  };

  const editQuestion = (id: number) => {
    const existing = form.questions.find((question) => question.id === id);
    if (!existing) {
      return;
    }
    const previousQuestions = cloneQuestionsForUndo(form.questions);
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.filter((question) => question.id !== id),
    }));
    setQuestionDraft(ensureOptions({ ...existing }));
    toast.info('Question ready to edit. Update it below and add it back to the test.', {
      action: {
        label: 'Undo',
        onClick: () =>
          setForm((prev) => ({
            ...prev,
            questions: previousQuestions,
          })),
      },
    });
  };

  const handleGenerateAiTest = () => {
    if (!jobSpec.trim()) {
      toast.error('Provide a job specification so the AI knows what to generate.');
      return;
    }
    setAiGenerating(true);
    const previousQuestions = cloneQuestionsForUndo(form.questions);
    setTimeout(() => {
      const generated = generateAiQuestions(jobSpec, aiQuestionCount);
      setForm((prev) => ({
        ...prev,
        questions: generated,
      }));
      setAiGenerating(false);
      toast.success('AI draft ready. Review each question before publishing.', {
        duration: 4000,
        action: {
          label: 'Undo',
          onClick: () =>
            setForm((prev) => ({
              ...prev,
              questions: previousQuestions,
            })),
        },
      });
    }, 900);
  };

  const handleSubmit = () => {
    if (!form.title.trim()) {
      toast.error('Give your test a title before saving.');
      return;
    }
    if (!form.category) {
      toast.error('Select a category for the test.');
      return;
    }
    if (form.questions.length === 0) {
      toast.error('Add at least one question to the test.');
      return;
    }
    onSubmit({
      ...form,
      title: form.title.trim(),
      description: form.description.trim(),
    });
  };

  return (
    <div className="space-y-8">
      <Card className="border-white/10 bg-white/[0.03]">
        <CardHeader>
          <CardTitle className="text-lg text-white">{mode === 'create' ? 'Create skill test' : 'Edit skill test'}</CardTitle>
          <CardDescription className="text-gray-400">
            Define the structure, auto-generate questions with AI, and fine-tune before sharing with candidates.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-4">
              <div>
                <Label className="text-white">Test title *</Label>
                <Input
                  value={form.title}
                  onChange={(event) => handleFormChange('title', event.target.value)}
                  placeholder="e.g. Inclusive Customer Success Assessment"
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                />
              </div>
              <div>
                <Label className="text-white">Category *</Label>
                <Select value={form.category} onValueChange={(value) => handleFormChange('category', value)}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent className="role-surface-employer border-white/10 bg-role-employer-overlay text-white backdrop-blur-xl">
                    {categories.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white">Status</Label>
                <Select value={form.status} onValueChange={(value) => handleFormChange('status', value as SkillTestStatus)}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="role-surface-employer border-white/10 bg-role-employer-overlay text-white backdrop-blur-xl">
                    {statuses.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-white">Duration (minutes)</Label>
                <Input
                  type="number"
                  min={5}
                  max={120}
                  value={form.duration}
                  onChange={(event) => handleFormChange('duration', Number(event.target.value))}
                  className="bg-white/10 border-white/20 text-white"
                />
              </div>
              <div>
                <Label className="text-white">Passing score (%)</Label>
                <Input
                  type="number"
                  min={10}
                  max={100}
                  value={form.passingScore}
                  onChange={(event) => handleFormChange('passingScore', Number(event.target.value))}
                  className="bg-white/10 border-white/20 text-white"
                />
              </div>
            </div>
          </div>
          <div>
            <Label className="text-white">Description</Label>
            <Textarea
              value={form.description}
              onChange={(event) => handleFormChange('description', event.target.value)}
              placeholder="Summarise what this assessment measures and how it relates to the role."
              className="min-h-[120px] bg-white/10 border-white/20 text-white placeholder:text-gray-400"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-sky-400/40 bg-[#0c1b2f] shadow-[0_0_45px_rgba(56,189,248,0.15)]">
        <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-sky-400 via-indigo-400 to-purple-400 text-[#06111f]">
              <Sparkles className="h-5 w-5" aria-hidden />
            </div>
            <div>
              <CardTitle className="text-white text-lg">Generate questions with AI</CardTitle>
              <CardDescription className="text-sky-100/80">
                Share the job specification and choose how many questions to draft automatically. You can tweak everything afterwards.
              </CardDescription>
            </div>
          </div>
          <Badge className="w-fit border-sky-400/40 bg-sky-400/10 text-sky-100">Preview only</Badge>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-[1fr_auto]">
            <div className="space-y-2">
              <Label className="text-sky-100">Job specification or requirements *</Label>
              <Textarea
                value={jobSpec}
                onChange={(event) => setJobSpec(event.target.value)}
                placeholder="Paste a short summary of the job responsibilities, ideal candidate profile, or competency framework."
                className="min-h-[120px] resize-none bg-[#13253f] text-sky-100 placeholder:text-sky-200/60 focus-visible:ring-2 focus-visible:ring-sky-400"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sky-100">Number of questions</Label>
              <Input
                type="number"
                min={3}
                max={12}
                value={aiQuestionCount}
                onChange={(event) => setAiQuestionCount(Number(event.target.value))}
                className="bg-[#13253f] text-white border-sky-500/40 focus-visible:ring-2 focus-visible:ring-sky-400"
              />
              <p className="text-xs text-sky-200/80">We recommend 5-8 questions for an initial screen.</p>
            </div>
          </div>
          <Button
            type="button"
            onClick={handleGenerateAiTest}
            disabled={aiGenerating}
            className="flex w-full items-center justify-center gap-2 bg-gradient-to-r from-sky-400 via-indigo-400 to-purple-400 text-[#041023] hover:from-sky-300 hover:via-indigo-300 hover:to-purple-300"
          >
            {aiGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lightbulb className="h-4 w-4" />}
            {aiGenerating ? 'Generating draft…' : 'Generate draft test'}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-white/[0.02]">
        <CardHeader>
          <CardTitle className="text-white">Current questions ({form.questions.length})</CardTitle>
          <CardDescription className="text-gray-400">
            Review, edit, or remove AI suggestions before publishing. Add your own questions at any time.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {form.questions.length === 0 && (
            <div className="rounded-lg border border-dashed border-white/20 p-6 text-center text-sm text-gray-400">
              No questions yet. Generate a draft or add one manually below.
            </div>
          )}
          {form.questions.map((question, index) => (
            <Card key={question.id} className="border-white/10 bg-white/[0.04]">
              <CardContent className="flex flex-col gap-4 p-4 md:flex-row md:items-start md:justify-between">
                <div className="flex-1 space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="border-white/20 text-gray-300">
                      Q{index + 1}
                    </Badge>
                    <Badge className="bg-[#ba9036]/20 text-[#f7ef8a] border-[#d2ac47]/50">{question.points} pts</Badge>
                    <Badge variant="outline" className="border-white/20 text-gray-300">
                      {question.type.replace('-', ' ')}
                    </Badge>
                  </div>
                  <p className="text-white">{question.text}</p>
                  {question.options && (
                    <div className="space-y-1 text-sm text-gray-300">
                      {question.options.map((option, optionIndex) => (
                        <div key={optionIndex} className="flex items-center gap-2">
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/10 text-xs">
                            {String.fromCharCode(65 + optionIndex)}
                          </span>
                          <span className={question.correctAnswer === option ? 'text-green-300' : undefined}>{option}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {question.type !== 'multiple-choice' && question.correctAnswer && (
                    <p className="text-sm text-gray-400">
                      <span className="text-gray-300">Reference answer:</span> {question.correctAnswer}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 md:flex-col md:items-stretch">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => editQuestion(question.id)}
                    className="border-white/20 text-white hover:bg-white/5"
                  >
                    <Edit className="mr-1 h-4 w-4" />
                    Edit
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeQuestion(question.id)}
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="mr-1 h-4 w-4" />
                    Remove
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-white/[0.03]">
        <CardHeader>
          <CardTitle className="text-white">Add or edit question</CardTitle>
          <CardDescription className="text-gray-400">
            Build your own questions or refine the AI suggestions before adding them to the draft.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label className="text-white">Question type</Label>
              <Select value={questionDraft.type} onValueChange={(value) => handleQuestionTypeChange(value as QuestionType)}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="role-surface-employer border-white/10 bg-role-employer-overlay text-white backdrop-blur-xl">
                  <SelectItem value="multiple-choice">Multiple choice</SelectItem>
                  <SelectItem value="short-answer">Short answer</SelectItem>
                  <SelectItem value="long-answer">Long answer</SelectItem>
                  <SelectItem value="true-false">True / False</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white">Points</Label>
              <Input
                type="number"
                min={1}
                max={50}
                value={questionDraft.points}
                onChange={(event) =>
                  setQuestionDraft((prev) => ({
                    ...prev,
                    points: Number(event.target.value),
                  }))
                }
                className="bg-white/10 border-white/20 text-white"
              />
            </div>
          </div>
          <div>
            <Label className="text-white">Question prompt *</Label>
            <Textarea
              value={questionDraft.text}
              onChange={(event) =>
                setQuestionDraft((prev) => ({
                  ...prev,
                  text: event.target.value,
                }))
              }
              placeholder="Write the question you want candidates to answer."
              className="min-h-[120px] bg-white/10 border-white/20 text-white placeholder:text-gray-400"
            />
          </div>

          {questionDraft.type === 'multiple-choice' && questionDraft.options && (
            <div className="space-y-3">
              <Label className="text-white">Answer options *</Label>
              {questionDraft.options.map((option, optionIndex) => (
                <div key={optionIndex} className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-xs text-gray-300">
                    {String.fromCharCode(65 + optionIndex)}
                  </div>
                  <Input
                    value={option}
                    onChange={(event) => handleQuestionOptionChange(optionIndex, event.target.value)}
                    className="bg-white/10 border-white/20 text-white"
                  />
                  <Button
                    type="button"
                    variant={questionDraft.correctAnswer === option ? 'default' : 'outline'}
                    size="sm"
                    onClick={() =>
                      setQuestionDraft((prev) => ({
                        ...prev,
                        correctAnswer: option,
                      }))
                    }
                    className={
                      questionDraft.correctAnswer === option
                        ? 'bg-green-500/80 text-black hover:bg-green-500'
                        : 'border-white/20 text-white hover:bg-white/5'
                    }
                  >
                    {questionDraft.correctAnswer === option ? 'Correct' : 'Mark correct'}
                  </Button>
                </div>
              ))}
            </div>
          )}

          {questionDraft.type === 'true-false' && (
            <div className="space-y-2">
              <Label className="text-white">Correct answer</Label>
              <RadioGroup
                value={typeof questionDraft.correctAnswer === 'string' ? questionDraft.correctAnswer : 'true'}
                onValueChange={(value) =>
                  setQuestionDraft((prev) => ({
                    ...prev,
                    correctAnswer: value,
                  }))
                }
                className="flex gap-6 text-white"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="true" id="true" />
                  <Label htmlFor="true" className="text-white">
                    True
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="false" id="false" />
                  <Label htmlFor="false" className="text-white">
                    False
                  </Label>
                </div>
              </RadioGroup>
            </div>
          )}

          {(questionDraft.type === 'short-answer' || questionDraft.type === 'long-answer') && (
            <div>
              <Label className="text-white">Reference answer (optional)</Label>
              <Textarea
                value={typeof questionDraft.correctAnswer === 'string' ? questionDraft.correctAnswer : ''}
                onChange={(event) =>
                  setQuestionDraft((prev) => ({
                    ...prev,
                    correctAnswer: event.target.value,
                  }))
                }
                placeholder="Share key points to guide scoring."
                className="min-h-[80px] bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              />
            </div>
          )}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Button
              type="button"
              variant="outline"
              className="border-white/20 text-white hover:bg-white/5"
              onClick={() => setQuestionDraft(ensureOptions(createEmptyQuestion()))}
            >
              <X className="mr-2 h-4 w-4" />
              Reset question editor
            </Button>
            <Button
              type="button"
              onClick={addQuestionToForm}
              className="flex items-center gap-2 bg-gold-gradient text-black hover:brightness-110"
            >
              <Plus className="h-4 w-4" />
              {questionDraft.id ? 'Update question' : 'Add question to test'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col gap-3 border-t border-white/10 pt-6 sm:flex-row sm:items-center sm:justify-between">
        <Button type="button" variant="outline" onClick={onCancel} className="border-white/20 text-white hover:bg-white/5">
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleSubmit}
          className="flex items-center gap-2 bg-gold-gradient text-black hover:brightness-110"
        >
          Continue
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
